import React, { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../supabase';
import { AttachmentUploader } from '../components/AttachmentUploader';
import { DropZone } from '../components/DropZone';
import { useAuth } from '../AuthContext';
import { C, S, getStatusBadgeStyle, formatDate, formatDateTime, formatCurrency, formatValuationBasis } from '../styles';
import { sendEmail } from '../utils/emailService';
import { variationSubmissionEmail } from '../utils/emailTemplates';
import { generateVariationPDF } from '../utils/generateVariationPDF';

const PROOF_CHAIN_ITEMS = [
  { key: 'instruction_status', label: 'Instruction', hint: 'EWN/CVI must exist to establish entitlement.' },
  { key: 'entitlement_status', label: 'Entitlement', hint: 'Written reason why works fall outside subcontract scope.' },
  { key: 'quantum_status', label: 'Quantum', hint: 'Signed daywork sheets or priced schedule of rates.' },
  { key: 'submission_status', label: 'Submission', hint: 'Formally submitted to MC with dated record.' },
  { key: 'application_status', label: 'Application', hint: 'Value included in interim application for payment.' },
];

const PROOF_STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'disputed', label: 'Disputed' },
  { value: 'na', label: 'N/A' },
];

const ATTACHMENT_TYPES = [
  { value: 'vq_document', label: 'VQ Document' },
  { value: 'drawing', label: 'Marked-up Drawing' },
  { value: 'email', label: 'Email / Correspondence' },
  { value: 'other', label: 'Other' },
];

const QS_VALUATION_OPTIONS = [
  { value: 'contract_rates', label: 'Contract Rates', desc: 'Using agreed schedule of rates' },
  { value: 'new_rates', label: 'New Rates', desc: 'New rates agreed or to be agreed' },
  { value: 'fair_valuation', label: 'Fair Valuation', desc: 'Fair and reasonable basis' },
  { value: 'dayworks', label: 'Dayworks', desc: 'Valued from signed daywork sheets' },
  { value: 'mixed', label: 'Mixed', desc: 'Combination of methods' },
];

function formatBytes(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

function RiskBadge({ level }) {
  const map = {
    low: { bg: '#f0fdf4', color: '#16a34a', border: '#bbf7d0', label: 'Low Risk' },
    medium: { bg: '#fffbeb', color: '#d97706', border: '#fde68a', label: 'Medium Risk' },
    high: { bg: '#fef2f2', color: '#dc2626', border: '#fecaca', label: 'High Risk' },
  };
  const s = map[level] || map.low;
  return (
    <span style={{ background: s.bg, color: s.color, border: `1px solid ${s.border}`, padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '700' }}>
      {s.label}
    </span>
  );
}

function ProofDot({ status }) {
  const map = {
    confirmed: { bg: C.success, icon: '✓' },
    disputed: { bg: C.danger, icon: '✗' },
    pending: { bg: '#d1d5db', icon: '○' },
    na: { bg: C.textLight, icon: '—' },
  };
  const s = map[status] || map.pending;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      width: '24px', height: '24px', borderRadius: '50%',
      background: s.bg, color: '#fff', fontSize: '12px', fontWeight: '700', flexShrink: 0,
    }}>{s.icon}</span>
  );
}

function FileTypeBadge({ type }) {
  const colors = {
    vq_document: { bg: '#dbeafe', color: '#1e40af' },
    drawing: { bg: '#ede9fe', color: '#6d28d9' },
    email: { bg: '#fef3c7', color: '#92400e' },
    other: { bg: '#f3f4f6', color: '#374151' },
  };
  const s = colors[type] || colors.other;
  const label = ATTACHMENT_TYPES.find(a => a.value === type)?.label || type;
  return (
    <span style={{ ...s, padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '600' }}>
      {label}
    </span>
  );
}

function InfoRow({ label, value }) {
  return (
    <div style={{ display: 'flex', gap: '8px', marginBottom: '8px', fontSize: '14px', flexWrap: 'wrap' }}>
      <span style={{ fontWeight: '600', color: C.textMuted, minWidth: '160px' }}>{label}</span>
      <span style={{ color: C.text, flex: 1 }}>{value || '—'}</span>
    </div>
  );
}

function varStatusBadge(status) {
  const map = {
    draft:      { background: '#f3f4f6', color: '#6b7280' },
    qs_review:  { background: '#fef3c7', color: '#92400e' },
    submitted:  { background: '#fef3c7', color: '#92400e' },
    agreed:     { background: '#d1fae5', color: '#065f46' },
    part_agreed: { background: '#dbeafe', color: '#1e40af' },
    disputed:   { background: '#fee2e2', color: '#991b1b' },
  };
  return map[status] || { background: '#f3f4f6', color: '#6b7280' };
}

export default function VariationDetail({ navigate, params }) {
  const { profile } = useAuth();
  const variationId = params?.variationId;
  const role = profile?.role || '';
  const isMcQs = role === 'mc_qs';
  const isProlineStaff = role === 'admin' || role === 'proline_sm' || role === 'proline_qs';

  const [variation, setVariation] = useState(null);
  const [revisions, setRevisions] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [linkedDayworks, setLinkedDayworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [mcQsName, setMcQsName] = useState('');
  const [mcQsEmail, setMcQsEmail] = useState('');
  const [mcQsCompany, setMcQsCompany] = useState('');
  const [showResendModal, setShowResendModal] = useState(false);
  const [resendName, setResendName] = useState('');
  const [resendEmail, setResendEmail] = useState('');
  const [resendCompany, setResendCompany] = useState('');
  const [resendCc, setResendCc] = useState(false);
  const [resendCcEmail, setResendCcEmail] = useState('');
  const [resendMessage, setResendMessage] = useState('');
  const [resendSending, setResendSending] = useState(false);
  const [resendToast, setResendToast] = useState('');
  const [submissionLog, setSubmissionLog] = useState([]);

  const [showResponseForm, setShowResponseForm] = useState(false);
  const [mcResponse, setMcResponse] = useState('agreed');
  const [mcResponseValue, setMcResponseValue] = useState('');
  const [mcResponseReason, setMcResponseReason] = useState('');

  const [proofChain, setProofChain] = useState({});
  const [proofEditing, setProofEditing] = useState(false);

  const [payapp, setPayapp] = useState({ payapp_number: '', payapp_date: '', payapp_certified_amount: '' });
  const [payappEditing, setPayappEditing] = useState(false);

  const [showRevisionForm, setShowRevisionForm] = useState(false);
  const [revValue, setRevValue] = useState('');
  const [revNotes, setRevNotes] = useState('');
  const [revAttachments, setRevAttachments] = useState([]);
  const [revUploading, setRevUploading] = useState(false);
  const revFileRef = useRef();

  const [bgUploading, setBgUploading] = useState(false);
  const [bgUploadCount, setBgUploadCount] = useState(0);
  const [bgUploadDone, setBgUploadDone] = useState(0);
  const [varResponseApproval, setVarResponseApproval] = useState(null);

  const [qsValue, setQsValue] = useState('');
  const [qsValuationBasis, setQsValuationBasis] = useState('contract_rates');
  const [qsBreakdown, setQsBreakdown] = useState('');
  const [qsProgrammeImpact, setQsProgrammeImpact] = useState(false);
  const [qsProgrammeImpactDays, setQsProgrammeImpactDays] = useState(0);
  const [qsPricingNotes, setQsPricingNotes] = useState('');
  const [qsAttachments, setQsAttachments] = useState([]);

  useEffect(() => {
    if (variationId) loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variationId]);

  useEffect(() => {
    if (variation && variation.status === 'qs_review') {
      if (variation.value) setQsValue(String(variation.value));
      if (variation.valuation_basis) setQsValuationBasis(variation.valuation_basis);
      if (variation.description) setQsBreakdown(variation.description);
      if (variation.programme_impact_days > 0) {
        setQsProgrammeImpact(true);
        setQsProgrammeImpactDays(variation.programme_impact_days);
      }
      if (variation.pricing_notes) setQsPricingNotes(variation.pricing_notes);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variation?.id]);

  // 8-second loading safety net
  useEffect(() => {
    if (!loading) return;
    const t = setTimeout(() => {
      setLoading(false);
      setError('Loading timed out — please refresh the page or sign in again.');
    }, 8000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  useEffect(() => {
    const pending = window.__pendingUploads?.[variationId];
    if (!pending?.length) return;
    delete window.__pendingUploads[variationId];
    uploadFilesInBackground(variationId, pending);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variationId]);

  async function loadData() {
    setLoading(true);
    setError('');
    try {
      // Fetch variation first — explicit FK hints required because variations has
      // two FKs to profiles (created_by and mc_responded_by).
      const { data: v, error: vErr } = await supabase
        .from('variations')
        .select('*, projects(id, name, reference), instructions(id, reference, type, description, reason_outside_scope, location, gridlines), profiles!variations_created_by_fkey(full_name), mc_responded_by_profile:profiles!variations_mc_responded_by_fkey(full_name, company)')
        .eq('id', variationId)
        .single();

      if (vErr) {
        console.error('Variation load error:', vErr);
        setError(vErr.message);
        return;
      }

      setVariation(v);
      setProofChain({
        instruction_status: v?.instruction_status || 'pending',
        entitlement_status: v?.entitlement_status || 'pending',
        quantum_status: v?.quantum_status || 'pending',
        submission_status: v?.submission_status || 'pending',
        application_status: v?.application_status || 'pending',
      });
      setPayapp({
        payapp_number: v?.payapp_number ?? '',
        payapp_date: v?.payapp_date ?? '',
        payapp_certified_amount: v?.payapp_certified_amount ?? '',
      });

      // Load secondary data — errors here don't hide the variation
      const [{ data: revs, error: revErr }, { data: atts, error: attErr }] = await Promise.all([
        supabase
          .from('variation_revisions')
          .select('*, profiles!variation_revisions_submitted_by_fkey(full_name)')
          .eq('variation_id', variationId)
          .order('revision_number'),
        supabase
          .from('variation_attachments')
          .select('*')
          .eq('variation_id', variationId)
          .order('uploaded_at'),
      ]);

      if (revErr) console.error('Revision load error:', revErr);
      if (attErr) console.error('Attachment load error:', attErr);

      setRevisions(revs || []);
      setAttachments(atts || []);

      if (v?.instruction_id) {
        const { data: dws } = await supabase
          .from('dayworks')
          .select('id, reference, date_of_works, status')
          .eq('instruction_id', v.instruction_id)
          .order('date_of_works');
        setLinkedDayworks(dws || []);
      }

      const { data: log } = await supabase
        .from('submission_log')
        .select('*, profiles!sent_by(full_name)')
        .eq('record_type', 'variation')
        .eq('record_id', variationId)
        .order('sent_at', { ascending: false });
      setSubmissionLog(log || []);

      const { data: responseApproval } = await supabase
        .from('variation_approvals')
        .select('full_name_confirmed, company_confirmed, actioned_at, action')
        .eq('variation_id', variationId)
        .order('actioned_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      setVarResponseApproval(responseApproval || null);
    } catch (err) {
      console.error('loadData unexpected error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleDownload(attachment) {
    try {
      const { data, error: dlErr } = await supabase.storage
        .from('variation-attachments')
        .download(attachment.storage_path);
      if (dlErr) throw dlErr;
      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = attachment.file_name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(`Download failed: ${err.message}`);
    }
  }

  async function handleMcResponse() {
    setError('');
    if (mcResponse === 'part_agreed' && !mcResponseValue) {
      setError('Please enter the agreed value for a part-agreed response.');
      return;
    }
    if ((mcResponse === 'disputed' || mcResponse === 'part_agreed') && !mcResponseReason.trim()) {
      setError('Please provide a reason.');
      return;
    }
    setSaving(true);
    try {
      const now = new Date().toISOString();
      const { error: updateErr } = await supabase
        .from('variations')
        .update({
          status: mcResponse,
          mc_response: mcResponse,
          mc_response_at: now,
          mc_response_value: mcResponse === 'part_agreed' ? parseFloat(mcResponseValue) : null,
          mc_response_reason: mcResponseReason || null,
          mc_responded_by: profile.id,
          mc_responded_at: now,
        })
        .eq('id', variationId);
      if (updateErr) throw updateErr;
      setShowResponseForm(false);
      setSuccess('Response recorded.');
      await loadData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleSaveProofChain() {
    setSaving(true);
    setError('');
    try {
      const { error: updateErr } = await supabase.from('variations').update(proofChain).eq('id', variationId);
      if (updateErr) throw updateErr;
      setSuccess('Proof chain updated.');
      setProofEditing(false);
      await loadData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleSavePayApp() {
    setSaving(true);
    setError('');
    try {
      const updates = {
        payapp_number: payapp.payapp_number ? parseInt(payapp.payapp_number, 10) : null,
        payapp_date: payapp.payapp_date || null,
        payapp_certified_amount: payapp.payapp_certified_amount ? parseFloat(payapp.payapp_certified_amount) : null,
      };
      if (payapp.payapp_number) updates.application_status = 'confirmed';
      const { error: updateErr } = await supabase.from('variations').update(updates).eq('id', variationId);
      if (updateErr) throw updateErr;
      setPayappEditing(false);
      setSuccess('Payment application updated.');
      await loadData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function fetchAttachments() {
    const { data } = await supabase
      .from('variation_attachments')
      .select('*')
      .eq('variation_id', variationId)
      .order('uploaded_at');
    setAttachments(data || []);
  }

  async function uploadFilesInBackground(recordId, files) {
    setBgUploading(true);
    setBgUploadCount(files.length);
    setBgUploadDone(0);
    for (const file of files) {
      try {
        const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
        const path = `${recordId}/${Date.now()}_${safeName}`;
        const buffer = await file.arrayBuffer();
        const { error: storageErr } = await supabase.storage
          .from('variation-attachments')
          .upload(path, buffer, { contentType: file.type || 'application/octet-stream', upsert: false });
        if (!storageErr) {
          await supabase.from('variation_attachments').insert({
            variation_id: recordId,
            uploaded_by: profile.id,
            file_name: file.name,
            file_size: file.size,
            file_type: file.type || 'application/octet-stream',
            storage_path: path,
          });
          setBgUploadDone(prev => prev + 1);
          fetchAttachments();
        } else {
          console.error('Background upload error:', storageErr.message);
        }
      } catch (err) {
        console.error('Background upload exception:', file.name, err.message);
      }
    }
    setBgUploading(false);
  }

  async function handleQsSaveDraft() {
    setSaving(true);
    setError('');
    try {
      const { error: updateErr } = await supabase.from('variations').update({
        value: qsValue ? parseFloat(qsValue) : null,
        valuation_basis: qsValuationBasis,
        description: qsBreakdown || null,
        programme_impact_days: qsProgrammeImpact ? (parseInt(qsProgrammeImpactDays, 10) || 0) : 0,
        pricing_notes: qsPricingNotes || null,
      }).eq('id', variationId);
      if (updateErr) throw updateErr;
      if (qsAttachments.length > 0) {
        window.__pendingUploads = window.__pendingUploads || {};
        window.__pendingUploads[variationId] = qsAttachments;
        setQsAttachments([]);
      }
      setSuccess('Pricing draft saved.');
      await loadData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleQsSubmitToMc() {
    if (!qsValue || parseFloat(qsValue) <= 0) { setError('A quoted value is required before submitting.'); return; }
    setSaving(true);
    setError('');
    try {
      await supabase.from('variations').update({
        value: parseFloat(qsValue),
        valuation_basis: qsValuationBasis,
        description: qsBreakdown || null,
        programme_impact_days: qsProgrammeImpact ? (parseInt(qsProgrammeImpactDays, 10) || 0) : 0,
        pricing_notes: qsPricingNotes || null,
        submitted_to_qs_at: new Date().toISOString(),
        submitted_to_qs_by: profile.id,
        quantum_status: 'confirmed',
      }).eq('id', variationId);
      if (qsAttachments.length > 0) {
        window.__pendingUploads = window.__pendingUploads || {};
        window.__pendingUploads[variationId] = qsAttachments;
        setQsAttachments([]);
      }
      await loadData();
      setShowSubmitModal(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleSubmitToMC() {
    if (!mcQsName.trim()) { setError('MC QS Name is required.'); return; }
    if (!mcQsEmail.trim()) { setError('MC QS Email is required.'); return; }
    if (!mcQsCompany.trim()) { setError('MC Company is required.'); return; }
    setSaving(true);
    setError('');
    try {
      const token = crypto.randomUUID();
      const now = new Date().toISOString();
      const portalUrl = `https://proline.project-management.uk/mc-portal/variation/${variationId}?token=${token}`;

      const { error: updateErr } = await supabase
        .from('variations')
        .update({
          status: 'submitted',
          submitted_at: now,
          submitted_to_name: mcQsName,
          submitted_to_email: mcQsEmail,
          submitted_to_company: mcQsCompany,
          submission_status: 'confirmed',
        })
        .eq('id', variationId);
      if (updateErr) throw updateErr;

      const { data: pu } = await supabase
        .from('project_users')
        .select('profiles(email, role)')
        .eq('project_id', variation.project_id);
      const notifyEmails = (pu || [])
        .map(u => u.profiles)
        .filter(p => p && (p.role === 'proline_qs' || p.role === 'admin'))
        .map(p => p.email)
        .filter(Boolean);

      const { error: invErr } = await supabase
        .from('mc_invitations')
        .insert({
          project_id: variation.project_id,
          email: mcQsEmail,
          full_name: mcQsName,
          company: mcQsCompany,
          token,
          type: 'variation',
          status: 'pending',
          created_by: profile.id,
          item_id: variationId,
          item_type: 'variation',
          notify_emails: notifyEmails,
        });
      if (invErr) throw invErr;

      const updatedVariation = { ...variation, submitted_at: now };
      const { subject, htmlBody } = variationSubmissionEmail(
        updatedVariation, variation.projects, variation.instructions, mcQsName, portalUrl
      );
      await sendEmail({ to: mcQsEmail, subject, htmlBody });

      await supabase.from('submission_log').insert({
        record_type: 'variation', record_id: variationId,
        sent_to_name: mcQsName, sent_to_email: mcQsEmail, sent_to_company: mcQsCompany,
        sent_by: profile.id, token, is_resend: false,
      });

      setShowSubmitModal(false);
      setMcQsName(''); setMcQsEmail(''); setMcQsCompany('');
      setSuccess('Variation submitted to MC QS.');
      await loadData();
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  const handleRevFiles = useCallback(async (files) => {
    const fileArr = Array.from(files);
    setRevUploading(true);
    for (const file of fileArr) {
      try {
        const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
        const path = `${variation?.project_id}/${Date.now()}_${safeName}`;
        const arrayBuffer = await file.arrayBuffer();
        const { error: storageErr } = await supabase.storage
          .from('variation-attachments')
          .upload(path, arrayBuffer, { contentType: file.type || 'application/octet-stream', upsert: false });
        if (storageErr) throw storageErr;
        setRevAttachments(prev => [...prev, {
          key: `${file.name}-${Date.now()}`,
          file_name: file.name,
          file_size: file.size,
          file_type: file.type,
          storage_path: path,
          attachment_type: 'vq_document',
        }]);
      } catch (err) {
        setError(`Upload failed for ${file.name}: ${err.message}`);
      }
    }
    setRevUploading(false);
  }, [variation?.project_id]);

  async function handleSubmitRevision() {
    if (!revValue || parseFloat(revValue) <= 0) { setError('Enter a valid value for this revision.'); return; }
    setSaving(true);
    setError('');
    try {
      const now = new Date().toISOString();
      const nextRevNum = (variation.current_revision || 0) + 1;

      const { data: rev, error: revErr } = await supabase
        .from('variation_revisions')
        .insert({
          variation_id: variationId,
          revision_number: nextRevNum,
          submitted_by: profile.id,
          value: parseFloat(revValue),
          notes: revNotes || null,
          status: 'submitted',
        })
        .select()
        .single();
      if (revErr) throw revErr;

      if (revAttachments.length > 0) {
        const rows = revAttachments.map(a => ({
          variation_id: variationId,
          revision_id: rev.id,
          file_name: a.file_name,
          file_size: a.file_size,
          file_type: a.file_type,
          storage_path: a.storage_path,
          attachment_type: a.attachment_type,
          uploaded_by: profile.id,
        }));
        const { error: attErr } = await supabase.from('variation_attachments').insert(rows);
        if (attErr) throw attErr;
      }

      await supabase.from('variations').update({
        value: parseFloat(revValue),
        status: 'submitted',
        current_revision: nextRevNum,
        submitted_at: now,
        submission_status: 'confirmed',
        vq_submitted_date: now.split('T')[0],
      }).eq('id', variationId);

      setShowRevisionForm(false);
      setRevValue('');
      setRevNotes('');
      setRevAttachments([]);
      setSuccess(`Revision ${nextRevNum} submitted.`);
      await loadData();
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  function openResendModal() {
    setResendName(variation.submitted_to_name || '');
    setResendEmail(variation.submitted_to_email || '');
    setResendCompany(variation.submitted_to_company || '');
    setResendCc(false);
    setResendCcEmail('');
    setResendMessage('Please find the following variation quotation submitted by Proline Internals Ltd for your review and response. Please use the secure link below to access the portal and record your decision.');
    setShowResendModal(true);
  }

  async function handleResend() {
    if (!resendName.trim()) { setError('Recipient name is required.'); return; }
    if (!resendEmail.trim()) { setError('Recipient email is required.'); return; }
    if (!resendCompany.trim()) { setError('Company is required.'); return; }
    setResendSending(true);
    setError('');
    try {
      const token = crypto.randomUUID();
      const portalUrl = `https://proline.project-management.uk/mc-portal/variation/${variationId}?token=${token}`;

      await supabase.from('mc_invitations').insert({
        project_id: variation.project_id,
        email: resendEmail, full_name: resendName, company: resendCompany,
        token, type: 'variation', status: 'pending', created_by: profile.id,
        item_id: variationId, item_type: 'variation', notify_emails: [],
      });

      let { subject, htmlBody } = variationSubmissionEmail(
        variation, variation.projects, variation.instructions, resendName, portalUrl
      );
      if (resendMessage.trim()) {
        const safeMsg = resendMessage.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/\n/g,'<br/>');
        const noteHtml = `<div style="background:#fffbeb;border-left:4px solid #f59e0b;padding:12px 16px;border-radius:0 6px 6px 0;margin-bottom:20px;"><p style="margin:0;font-size:13px;color:#78350f;line-height:1.6;">${safeMsg}</p></div>`;
        htmlBody = htmlBody.replace('<h2 style=', `${noteHtml}<h2 style=`);
      }
      const toList = resendCc && resendCcEmail.trim() ? [resendEmail, resendCcEmail.trim()] : resendEmail;
      await sendEmail({ to: toList, subject, htmlBody });

      await supabase.from('submission_log').insert({
        record_type: 'variation', record_id: variationId,
        sent_to_name: resendName, sent_to_email: resendEmail, sent_to_company: resendCompany,
        sent_by: profile.id, token, is_resend: true,
      });

      setShowResendModal(false);
      setResendToast(`Email sent to ${resendName} at ${resendEmail}`);
      setTimeout(() => setResendToast(''), 5000);
      const { data: log } = await supabase
        .from('submission_log').select('*, profiles(full_name)')
        .eq('record_type', 'variation').eq('record_id', variationId)
        .order('sent_at', { ascending: false });
      setSubmissionLog(log || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setResendSending(false);
    }
  }

  async function handleDownloadPDF() {
    await generateVariationPDF({
      variation,
      project: variation.projects,
      instruction: variation.instructions,
      revisions,
    });
  }

  if (loading) return <div style={S.spinner}>Loading variation...</div>;
  if (!variation) return (
    <div style={S.pageContainer}>
      <div style={S.alertError}>{error || 'Variation not found.'}</div>
      <button onClick={() => navigate('projects', {})} style={{ ...S.btn, ...S.btnGhost }}>← Back</button>
    </div>
  );

  const statusStyle = getStatusBadgeStyle(variation.status);
  const confirmedCount = Object.values(proofChain).filter(v => v === 'confirmed').length;
  const totalSteps = PROOF_CHAIN_ITEMS.length;
  const canResend = isProlineStaff && ['submitted', 'agreed', 'part_agreed', 'disputed'].includes(variation.status) && !!variation.submitted_at;

  let daysElapsed = null;
  if (variation.notification_date) {
    daysElapsed = Math.floor((new Date() - new Date(variation.notification_date)) / 86400000);
  }

  const latestRev = revisions.length > 0 ? revisions[revisions.length - 1] : null;
  const currentRevAttachments = latestRev
    ? attachments.filter(a => a.revision_id === latestRev.id)
    : attachments.filter(a => !a.revision_id);

  return (
    <div style={S.pageContainer}>
      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <button
          onClick={() => navigate('project-detail', { projectId: variation.project_id })}
          style={{ ...S.btn, ...S.btnGhost, ...S.btnSm }}
        >
          ← Back to Project
        </button>
        {variation.instruction_id && (
          <button
            onClick={() => navigate('instruction-detail', { instructionId: variation.instruction_id })}
            style={{ ...S.btn, ...S.btnGhost, ...S.btnSm }}
          >
            ← Back to Instruction
          </button>
        )}
      </div>

      {error && <div style={S.alertError}>{error}</div>}
      {success && <div style={S.alertSuccess}>{success}</div>}

      {bgUploading && (
        <div style={{
          background: '#f5f5f5', border: '1px solid #d1d5db',
          borderRadius: '6px', padding: '10px 16px', marginBottom: '16px',
          display: 'flex', alignItems: 'center', gap: '10px',
        }}>
          <span>⏳</span>
          <span style={{ fontSize: '14px', color: '#111111' }}>
            Uploading attachments… {bgUploadDone} of {bgUploadCount} complete
          </span>
        </div>
      )}

      {/* ── HEADER PANEL ── */}
      <div style={{ background: '#000000', borderRadius: '8px', padding: '24px 28px', marginBottom: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
          {/* Left: identity + submission info */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '8px' }}>
              <span style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', padding: '6px 16px', borderRadius: '6px', fontFamily: 'monospace', fontSize: '20px', fontWeight: '700', letterSpacing: '0.08em' }}>
                {variation.reference}
              </span>
              <span style={{ ...varStatusBadge(variation.status), padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '700', letterSpacing: '0.04em' }}>
                {(variation.status || '').replace(/_/g, ' ').toUpperCase()}
              </span>
              {variation.created_at && (
                <span style={{ background: 'rgba(255,255,255,0.1)', color: '#e5e7eb', padding: '5px 12px', borderRadius: '6px', fontSize: '14px', fontWeight: '600' }}>
                  {formatDate(variation.created_at)}
                </span>
              )}
              {variation.current_revision > 0 && (
                <span style={{ background: 'rgba(255,255,255,0.15)', color: '#e5e7eb', padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600' }}>
                  Rev {variation.current_revision}
                </span>
              )}
              {variation.risk_level && <RiskBadge level={variation.risk_level} />}
              {variation.payapp_number && (
                <span style={{ background: '#fef3c7', color: '#92400e', padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '700' }}>
                  PayApp #{variation.payapp_number}
                </span>
              )}
            </div>
            {variation.title && (
              <div style={{ fontSize: '15px', fontWeight: '500', color: '#e5e7eb', marginBottom: '10px' }}>
                {variation.title}
              </div>
            )}
            <div style={{ fontSize: '13px', color: '#94a3b8', display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '12px' }}>
              <div><span style={{ color: '#cbd5e1' }}>Project:</span> {variation.projects?.reference} — {variation.projects?.name}</div>
              {variation.instructions?.reference && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ color: '#cbd5e1' }}>Instruction:</span>
                  <button onClick={() => navigate('instruction-detail', { instructionId: variation.instruction_id })}
                    style={{ background: 'rgba(255,255,255,0.1)', color: '#a5b4fc', border: 'none', borderRadius: '4px', padding: '2px 8px', cursor: 'pointer', fontFamily: 'monospace', fontWeight: '700', fontSize: '12px' }}>
                    {variation.instructions.reference}
                  </button>
                  <span style={{ color: '#6b7280', fontSize: '12px' }}>({variation.instructions.type})</span>
                </div>
              )}
              <div>
                <span style={{ color: '#cbd5e1' }}>Value:</span>{' '}
                <span style={{ color: '#fff', fontWeight: '700', fontSize: '16px' }}>{variation.value ? formatCurrency(variation.value) : '—'}</span>
                <span style={{ color: '#64748b', fontSize: '12px', marginLeft: '6px' }}>{formatValuationBasis(variation.valuation_basis)}</span>
              </div>
              {variation.mc_response_value && (
                <div><span style={{ color: '#cbd5e1' }}>MC Agreed:</span> <span style={{ color: '#fbbf24', fontWeight: '600' }}>{formatCurrency(variation.mc_response_value)}</span></div>
              )}
            </div>
            {variation.submitted_at && (
              <div style={{ padding: '10px 14px', background: 'rgba(255,255,255,0.06)', borderRadius: '6px', marginBottom: '8px' }}>
                <div style={{ fontSize: '11px', color: '#64748b', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>Submitted to Main Contractor</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: '4px', fontSize: '13px' }}>
                  {variation.submitted_to_name && <div><span style={{ color: '#94a3b8' }}>To: </span><span style={{ color: '#fff', fontWeight: '600' }}>{variation.submitted_to_name}</span></div>}
                  {variation.submitted_to_company && <div><span style={{ color: '#94a3b8' }}>Company: </span><span style={{ color: '#e5e7eb' }}>{variation.submitted_to_company}</span></div>}
                  <div><span style={{ color: '#94a3b8' }}>Date: </span><span style={{ color: '#e5e7eb' }}>{formatDateTime(variation.submitted_at)}</span></div>
                  {variation.profiles?.full_name && <div><span style={{ color: '#94a3b8' }}>By: </span><span style={{ color: '#e5e7eb' }}>{variation.profiles.full_name}</span></div>}
                </div>
              </div>
            )}
            {variation.mc_response && (() => {
              const isAgreed = variation.mc_response === 'agreed';
              const isDisputed = variation.mc_response === 'disputed';
              const bg = isAgreed ? 'rgba(16,185,129,0.15)' : isDisputed ? 'rgba(220,38,38,0.15)' : 'rgba(217,119,6,0.15)';
              const border = isAgreed ? 'rgba(16,185,129,0.3)' : isDisputed ? 'rgba(220,38,38,0.3)' : 'rgba(217,119,6,0.3)';
              const labelColor = isAgreed ? '#6ee7b7' : isDisputed ? '#fca5a5' : '#fde68a';
              const valueColor = isAgreed ? '#a7f3d0' : isDisputed ? '#fecaca' : '#fef3c7';
              const responderName = varResponseApproval?.full_name_confirmed || variation.mc_responded_by_profile?.full_name;
              const responderCompany = varResponseApproval?.company_confirmed || variation.mc_responded_by_profile?.company;
              return (
                <div style={{ padding: '10px 14px', background: bg, border: `1px solid ${border}`, borderRadius: '6px', marginBottom: '8px' }}>
                  <div style={{ fontSize: '11px', color: labelColor, fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>
                    MC Response — {variation.mc_response.replace(/_/g, ' ').toUpperCase()}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: '4px', fontSize: '13px' }}>
                    {responderName
                      ? <div><span style={{ color: '#94a3b8' }}>Responded by: </span><span style={{ color: valueColor, fontWeight: '600' }}>{responderName}{responderCompany ? ` — ${responderCompany}` : ''}</span></div>
                      : <div><span style={{ color: '#fca5a5', fontWeight: '600' }}>Responder details not recorded</span></div>
                    }
                    {variation.mc_response_at && <div><span style={{ color: '#94a3b8' }}>Date: </span><span style={{ color: valueColor }}>{formatDateTime(variation.mc_response_at)}</span></div>}
                    {variation.mc_response_reason && <div style={{ gridColumn: '1 / -1' }}><span style={{ color: '#94a3b8' }}>Note: </span><span style={{ color: valueColor }}>{variation.mc_response_reason}</span></div>}
                  </div>
                </div>
              );
            })()}
            {daysElapsed !== null && daysElapsed > 14 && (
              <div style={{
                padding: '10px 14px', borderRadius: '6px',
                background: daysElapsed > 28 ? 'rgba(220,38,38,0.2)' : 'rgba(217,119,6,0.2)',
                border: `1px solid ${daysElapsed > 28 ? 'rgba(220,38,38,0.5)' : 'rgba(217,119,6,0.5)'}`,
                color: daysElapsed > 28 ? '#fca5a5' : '#fde68a',
                fontSize: '13px', fontWeight: '600',
              }}>
                {daysElapsed > 28 ? '🔴' : '⚠'} Instruction received {daysElapsed} days ago.
                {daysElapsed > 28
                  ? ' Immediate action required — JCT notification period may have expired.'
                  : ' Submit without undue delay.'}
              </div>
            )}
          </div>
          {/* Right: buttons */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'stretch', minWidth: '210px' }}>
            <button onClick={handleDownloadPDF} style={{ ...S.btn, background: '#374151', color: '#fff', border: 'none', fontSize: '13px', fontWeight: '600', justifyContent: 'center' }}>
              Download PDF
            </button>
            {canResend && (
              <button onClick={openResendModal} style={{ ...S.btn, background: 'transparent', color: '#fff', border: '1px solid rgba(255,255,255,0.55)', fontSize: '13px', fontWeight: '600', justifyContent: 'center' }}>
                Resend / Send to Another Recipient
              </button>
            )}
            {isProlineStaff && variation.status === 'draft' && (
              <button
                onClick={() => navigate('variation-edit', { variationId: variation.id, projectId: variation.project_id })}
                style={{ ...S.btn, background: 'rgba(255,255,255,0.1)', color: '#e5e7eb', border: '1px solid rgba(255,255,255,0.2)', fontSize: '13px' }}
              >
                Edit Variation
              </button>
            )}
            {isProlineStaff && variation.status === 'draft' && (
              <button
                onClick={() => setShowSubmitModal(true)}
                disabled={saving}
                style={{ ...S.btn, background: C.success, color: '#fff', border: 'none', fontSize: '13px', opacity: saving ? 0.7 : 1 }}
              >
                Submit to MC
              </button>
            )}
          </div>
        </div>

        {showSubmitModal && (
          <div style={{ marginTop: '16px', padding: '20px', background: 'rgba(255,255,255,0.06)', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.15)' }}>
            <h4 style={{ margin: '0 0 16px 0', fontSize: '15px', fontWeight: '700', color: '#fff' }}>Submit Variation to Main Contractor</h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '12px', marginBottom: '12px' }}>
              <div style={S.formGroup}>
                <label style={{ ...S.label, color: '#94a3b8' }}>MC QS Name *</label>
                <input style={S.input} value={mcQsName} onChange={e => setMcQsName(e.target.value)} placeholder="e.g. John Smith" />
              </div>
              <div style={S.formGroup}>
                <label style={{ ...S.label, color: '#94a3b8' }}>MC QS Email *</label>
                <input type="email" style={S.input} value={mcQsEmail} onChange={e => setMcQsEmail(e.target.value)} placeholder="qs@contractor.co.uk" />
              </div>
              <div style={S.formGroup}>
                <label style={{ ...S.label, color: '#94a3b8' }}>MC Company *</label>
                <input style={S.input} value={mcQsCompany} onChange={e => setMcQsCompany(e.target.value)} placeholder="e.g. Main Contractor Ltd" />
              </div>
            </div>
            <div style={{ fontSize: '13px', color: '#94a3b8', background: 'rgba(255,255,255,0.04)', padding: '10px 14px', borderRadius: '6px', marginBottom: '12px' }}>
              An email will be sent to the MC QS with a secure link to review and respond to this variation quotation.
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={handleSubmitToMC} disabled={saving} style={{ ...S.btn, ...S.btnPrimary, opacity: saving ? 0.7 : 1 }}>
                {saving ? 'Submitting…' : 'Send to MC QS'}
              </button>
              <button onClick={() => { setShowSubmitModal(false); setMcQsName(''); setMcQsEmail(''); setMcQsCompany(''); }} style={{ ...S.btn, background: 'rgba(255,255,255,0.1)', color: '#e5e7eb', border: '1px solid rgba(255,255,255,0.2)' }}>
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── QS PRICING WORKSPACE ── */}
      {isProlineStaff && (role === 'proline_qs' || role === 'admin') && variation.status === 'qs_review' && (
        <div style={{ ...S.card, marginBottom: '20px', border: '2px solid #f59e0b' }}>
          <div style={{ background: '#fffbeb', borderRadius: '6px 6px 0 0', padding: '14px 20px', margin: '-20px -20px 20px -20px', borderBottom: '1px solid #f59e0b', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '18px' }}>⚡</span>
            <div>
              <div style={{ fontSize: '14px', fontWeight: '700', color: '#92400e' }}>ACTION REQUIRED — QS Pricing Workspace</div>
              <div style={{ fontSize: '12px', color: '#78350f' }}>This variation was raised by site and requires QS pricing before submission to the MC.</div>
            </div>
          </div>

          {/* Site info read-only */}
          {(variation.description || variation.scope_notes || variation.site_notes) && (
            <div style={{ background: '#f8fafc', borderRadius: '6px', padding: '14px 16px', marginBottom: '20px', border: `1px solid ${C.border}` }}>
              <div style={{ fontSize: '12px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '10px' }}>From Site Manager</div>
              {variation.description && (
                <div style={{ marginBottom: '8px' }}>
                  <div style={{ fontSize: '12px', color: C.textMuted, marginBottom: '2px' }}>Works Description</div>
                  <div style={{ fontSize: '13px', color: C.text }}>{variation.description}</div>
                </div>
              )}
              {variation.scope_notes && (
                <div style={{ marginBottom: '8px' }}>
                  <div style={{ fontSize: '12px', color: C.textMuted, marginBottom: '2px' }}>Why It's a Variation</div>
                  <div style={{ fontSize: '13px', color: C.text }}>{variation.scope_notes}</div>
                </div>
              )}
              {variation.site_notes && (
                <div>
                  <div style={{ fontSize: '12px', color: C.textMuted, marginBottom: '2px' }}>Notes for QS</div>
                  <div style={{ fontSize: '13px', color: C.text }}>{variation.site_notes}</div>
                </div>
              )}
            </div>
          )}

          {/* Pricing fields */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
            <div style={S.formGroup}>
              <label style={S.label}>Quoted Value (£) <span style={{ color: C.danger }}>*</span></label>
              <div style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: C.textMuted, fontWeight: '600' }}>£</span>
                <input
                  type="number" step="0.01"
                  style={{ ...S.input, paddingLeft: '24px' }}
                  value={qsValue}
                  onChange={e => setQsValue(e.target.value)}
                  placeholder="0.00"
                />
              </div>
              {qsValue && parseFloat(qsValue) > 0 && (
                <div style={{ fontSize: '13px', color: C.success, fontWeight: '600', marginTop: '4px' }}>{formatCurrency(parseFloat(qsValue))}</div>
              )}
            </div>
            <div style={S.formGroup}>
              <label style={S.label}>Programme Impact</label>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
                {['No', 'Yes'].map(opt => (
                  <button key={opt} type="button"
                    onClick={() => setQsProgrammeImpact(opt === 'Yes')}
                    style={{ ...S.btn, ...(qsProgrammeImpact === (opt === 'Yes') ? S.btnPrimary : S.btnGhost), ...S.btnSm }}>
                    {opt}
                  </button>
                ))}
              </div>
              {qsProgrammeImpact && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input type="number" min="0" style={{ ...S.input, width: '80px' }} value={qsProgrammeImpactDays} onChange={e => setQsProgrammeImpactDays(e.target.value)} />
                  <span style={{ fontSize: '13px', color: C.textMuted }}>Man Days</span>
                </div>
              )}
            </div>
          </div>

          <div style={S.formGroup}>
            <label style={S.label}>Valuation Basis</label>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {QS_VALUATION_OPTIONS.map(opt => (
                <button key={opt.value} type="button"
                  onClick={() => setQsValuationBasis(opt.value)}
                  style={{
                    ...S.btn, ...S.btnSm,
                    ...(qsValuationBasis === opt.value ? S.btnPrimary : S.btnGhost),
                  }}>
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div style={S.formGroup}>
            <label style={S.label}>Pricing Breakdown</label>
            <textarea
              style={{ ...S.textarea, minHeight: '80px' }}
              value={qsBreakdown}
              onChange={e => setQsBreakdown(e.target.value)}
              placeholder="Labour, materials, plant build-up or rate calculation…"
            />
          </div>

          <div style={S.formGroup}>
            <label style={S.label}>Pricing Notes (internal)</label>
            <textarea
              style={{ ...S.textarea, minHeight: '60px' }}
              value={qsPricingNotes}
              onChange={e => setQsPricingNotes(e.target.value)}
              placeholder="Any notes on the pricing approach, assumptions, or risks…"
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ ...S.label, marginBottom: '8px' }}>Supporting Documents</label>
            <DropZone
              onFilesSelected={files => setQsAttachments(prev => {
                const existing = new Set(prev.map(f => f.name));
                return [...prev, ...files.filter(f => !existing.has(f.name))];
              })}
              files={qsAttachments}
              onRemove={i => setQsAttachments(prev => prev.filter((_, j) => j !== i))}
              accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
              multiple={true}
              label="Pricing schedules, drawings, build-ups"
            />
          </div>

          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', paddingTop: '8px', borderTop: `1px solid ${C.border}` }}>
            <button
              onClick={handleQsSaveDraft}
              disabled={saving}
              style={{ ...S.btn, ...S.btnGhost, opacity: saving ? 0.7 : 1 }}
            >
              {saving ? 'Saving…' : 'Save Draft'}
            </button>
            <button
              onClick={handleQsSubmitToMc}
              disabled={saving}
              style={{ ...S.btn, ...S.btnPrimary, opacity: saving ? 0.7 : 1 }}
            >
              {saving ? 'Preparing…' : 'Submit to MC →'}
            </button>
          </div>
        </div>
      )}

      {/* ── RESEND MODAL ── */}
      {showResendModal && (
        <div style={{ ...S.card, marginBottom: '20px', background: '#fff', border: '2px solid #000' }}>
          <h4 style={{ margin: '0 0 16px 0', fontSize: '15px', fontWeight: '700', color: C.text }}>
            Resend / Send to Another Recipient
          </h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '12px', marginBottom: '12px' }}>
            <div style={S.formGroup}>
              <label style={S.label}>Recipient Name *</label>
              <input style={S.input} value={resendName} onChange={e => setResendName(e.target.value)} placeholder="e.g. John Smith" />
            </div>
            <div style={S.formGroup}>
              <label style={S.label}>Recipient Email *</label>
              <input type="email" style={S.input} value={resendEmail} onChange={e => setResendEmail(e.target.value)} placeholder="qs@contractor.co.uk" />
            </div>
            <div style={S.formGroup}>
              <label style={S.label}>Company *</label>
              <input style={S.input} value={resendCompany} onChange={e => setResendCompany(e.target.value)} placeholder="e.g. Main Contractor Ltd" />
            </div>
          </div>
          <div style={{ ...S.formGroup, marginBottom: '10px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: C.text, cursor: 'pointer' }}>
              <input type="checkbox" checked={resendCc} onChange={e => setResendCc(e.target.checked)} />
              CC another email address
            </label>
            {resendCc && (
              <input
                type="email" style={{ ...S.input, marginTop: '8px' }}
                value={resendCcEmail} onChange={e => setResendCcEmail(e.target.value)}
                placeholder="cc@example.com"
              />
            )}
          </div>
          <div style={S.formGroup}>
            <label style={S.label}>Covering Message (optional)</label>
            <textarea
              style={{ ...S.textarea, minHeight: '80px' }}
              value={resendMessage}
              onChange={e => setResendMessage(e.target.value)}
            />
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={handleResend}
              disabled={resendSending}
              style={{ ...S.btn, ...S.btnPrimary, opacity: resendSending ? 0.7 : 1 }}
            >
              {resendSending ? 'Sending…' : 'Send Email'}
            </button>
            <button onClick={() => setShowResendModal(false)} style={{ ...S.btn, ...S.btnGhost }}>Cancel</button>
          </div>
        </div>
      )}

      {/* ── PROJECT & INSTRUCTION PANEL ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '16px', marginBottom: '20px' }}>
        <div style={S.card}>
          <h3 style={{ ...S.sectionTitle, marginBottom: '14px' }}>Project Details</h3>
          <InfoRow label="Project" value={`${variation.projects?.reference} — ${variation.projects?.name}`} />
          <InfoRow label="Raised By" value={variation.profiles?.full_name} />
          <InfoRow label="Created" value={formatDateTime(variation.created_at)} />
          {variation.submitted_at && <InfoRow label="Submitted" value={formatDateTime(variation.submitted_at)} />}
          {variation.vq_submitted_date && <InfoRow label="VQ Date" value={formatDate(variation.vq_submitted_date)} />}
          {variation.notification_date && (
            <InfoRow
              label="Notified"
              value={`${formatDate(variation.notification_date)}${daysElapsed !== null ? ` (${daysElapsed} days ago)` : ''}`}
            />
          )}
        </div>

        {variation.instructions ? (
          <div style={S.card}>
            <h3 style={{ ...S.sectionTitle, marginBottom: '14px' }}>Linked Instruction</h3>
            <div
              onClick={() => navigate('instruction-detail', { instructionId: variation.instruction_id })}
              style={{
                padding: '12px 14px', border: `1px solid ${C.border}`,
                borderRadius: '6px', cursor: 'pointer', background: '#f9fafb', marginBottom: '10px',
                transition: 'border-color 0.1s',
              }}
              onMouseEnter={e => e.currentTarget.style.borderColor = C.accent}
              onMouseLeave={e => e.currentTarget.style.borderColor = C.border}
            >
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '6px' }}>
                <span style={{
                  background: C.header, color: '#fff', padding: '2px 10px',
                  borderRadius: '4px', fontFamily: 'monospace', fontWeight: '700', fontSize: '13px',
                }}>
                  {variation.instructions.reference}
                </span>
                <span style={{
                  background: variation.instructions.type === 'EWN' ? '#dbeafe' : variation.instructions.type === 'IR' ? '#d1fae5' : '#fce7f3',
                  color: variation.instructions.type === 'EWN' ? '#1e40af' : variation.instructions.type === 'IR' ? '#065f46' : '#9d174d',
                  padding: '1px 6px', borderRadius: '3px', fontSize: '11px', fontWeight: '700',
                }}>
                  {variation.instructions.type}
                </span>
              </div>
              {variation.instructions.location && (
                <div style={{ fontSize: '12px', color: C.textMuted, marginBottom: '4px' }}>
                  Location: {variation.instructions.location}
                </div>
              )}
              <div style={{ fontSize: '12px', color: C.textMuted }}>
                {variation.instructions.description?.substring(0, 80)}
              </div>
            </div>
            {variation.instructions.reason_outside_scope ? (
              <div>
                <div style={{ fontSize: '12px', fontWeight: '600', color: C.textMuted, marginBottom: '4px' }}>Entitlement:</div>
                <div style={{ fontSize: '13px', color: C.text, lineHeight: '1.5' }}>
                  {variation.instructions.reason_outside_scope}
                </div>
              </div>
            ) : (
              <div style={{ fontSize: '12px', color: C.warning, padding: '6px 10px', background: '#fffbeb', borderRadius: '4px', border: '1px solid #fde68a' }}>
                ⚠ No reason outside scope documented on instruction
              </div>
            )}
          </div>
        ) : (
          <div style={S.card}>
            <h3 style={{ ...S.sectionTitle, marginBottom: '8px' }}>Linked Instruction</h3>
            <div style={{ color: C.textMuted, fontSize: '14px' }}>No instruction linked.</div>
          </div>
        )}
      </div>

      {/* ── SCOPE PANEL ── */}
      <div style={{ ...S.card, marginBottom: '20px' }}>
        <h3 style={{ ...S.sectionTitle, marginBottom: '16px' }}>Scope of Works</h3>
        {variation.description && (
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '12px', fontWeight: '600', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>Description</div>
            <p style={{ fontSize: '14px', color: C.text, lineHeight: '1.7', whiteSpace: 'pre-wrap', margin: 0 }}>{variation.description}</p>
          </div>
        )}
        {variation.scope_notes && (
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '12px', fontWeight: '600', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>Scope Notes</div>
            <p style={{ fontSize: '14px', color: C.text, lineHeight: '1.7', whiteSpace: 'pre-wrap', margin: 0 }}>{variation.scope_notes}</p>
          </div>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '16px', paddingTop: variation.description || variation.scope_notes ? '12px' : '0', borderTop: variation.description || variation.scope_notes ? `1px solid ${C.border}` : 'none' }}>
          <div>
            <div style={{ fontSize: '12px', fontWeight: '600', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>Valuation Basis</div>
            <div style={{ fontSize: '14px', color: C.text }}>{formatValuationBasis(variation.valuation_basis)}</div>
          </div>
          <div>
            <div style={{ fontSize: '12px', fontWeight: '600', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>Programme Impact</div>
            <div style={{ fontSize: '14px', color: C.text }}>
              {variation.programme_impact_days > 0 ? `${variation.programme_impact_days} Man Days` : 'None'}
            </div>
          </div>
        </div>
      </div>

      {/* ── REVISION HISTORY ── */}
      <div style={{ ...S.card, marginBottom: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h3 style={S.sectionTitle}>Revision History ({revisions.length})</h3>
          {isProlineStaff && !showRevisionForm && !['agreed', 'part_agreed'].includes(variation.status) && (
            <button onClick={() => setShowRevisionForm(true)} style={{ ...S.btn, ...S.btnOutline, ...S.btnSm }}>
              + Submit Revision
            </button>
          )}
        </div>

        {revisions.length === 0 ? (
          <div style={{ color: C.textMuted, fontSize: '14px', padding: '12px 0' }}>No revisions submitted yet.</div>
        ) : (
          <div style={S.tableWrapper}>
            <table style={S.table}>
              <thead>
                <tr>
                  {['Rev', 'Date Submitted', 'Submitted By', 'Value', 'Status', 'Documents'].map(h => (
                    <th key={h} style={S.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {revisions.map(rev => {
                  const revAtts = attachments.filter(a => a.revision_id === rev.id);
                  const revStatusStyle = getStatusBadgeStyle(rev.status);
                  return (
                    <tr key={rev.id}>
                      <td style={S.td}>
                        <span style={{ fontWeight: '700', fontFamily: 'monospace', fontSize: '13px' }}>
                          Rev {rev.revision_number}
                        </span>
                      </td>
                      <td style={S.td}>{formatDate(rev.submitted_at)}</td>
                      <td style={S.td}>{rev.profiles?.full_name || '—'}</td>
                      <td style={{ ...S.td, fontWeight: '600' }}>{formatCurrency(rev.value)}</td>
                      <td style={S.td}>
                        <span style={{ ...S.badge, ...revStatusStyle }}>{rev.status}</span>
                      </td>
                      <td style={S.td}>
                        {revAtts.length > 0 ? (
                          <span style={{ fontSize: '12px', color: C.accent, fontWeight: '600' }}>
                            {revAtts.length} file{revAtts.length > 1 ? 's' : ''}
                          </span>
                        ) : (
                          <span style={{ fontSize: '12px', color: C.textMuted }}>—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {showRevisionForm && (
          <div style={{ marginTop: '16px', padding: '16px', background: '#f9fafb', borderRadius: '6px', border: `1px solid ${C.border}` }}>
            <h4 style={{ fontWeight: '600', fontSize: '14px', color: C.text, marginBottom: '14px' }}>
              Submit Revision {(variation.current_revision || 0) + 1}
            </h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
              <div style={S.formGroup}>
                <label style={S.label}>Revised Value (£) *</label>
                <div style={{ position: 'relative' }}>
                  <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: C.textMuted, fontWeight: '600' }}>£</span>
                  <input
                    type="number" step="0.01"
                    style={{ ...S.input, paddingLeft: '24px' }}
                    value={revValue}
                    onChange={e => setRevValue(e.target.value)}
                    placeholder="0.00"
                  />
                </div>
              </div>
            </div>
            <div style={S.formGroup}>
              <label style={S.label}>Revision Notes / Changes from Previous</label>
              <textarea
                style={{ ...S.textarea, minHeight: '70px' }}
                value={revNotes}
                onChange={e => setRevNotes(e.target.value)}
                placeholder="Describe what has changed from the previous revision..."
              />
            </div>
            <div style={S.formGroup}>
              <label style={S.label}>Upload Documents</label>
              <div
                onClick={() => revFileRef.current?.click()}
                style={{
                  border: `2px dashed ${C.borderDark}`, borderRadius: '6px',
                  padding: '16px', textAlign: 'center', cursor: 'pointer', background: '#fafafa',
                }}
              >
                <div style={{ fontSize: '13px', color: C.textMuted }}>
                  {revUploading ? 'Uploading...' : 'Click to upload revised VQ documents'}
                </div>
              </div>
              <input
                ref={revFileRef} type="file" multiple
                accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
                style={{ display: 'none' }}
                onChange={e => handleRevFiles(e.target.files)}
              />
              {revAttachments.length > 0 && (
                <div style={{ marginTop: '8px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {revAttachments.map(a => (
                    <div key={a.key} style={{ fontSize: '12px', background: '#dbeafe', color: '#1e40af', padding: '3px 8px', borderRadius: '4px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      {a.file_name}
                      <button
                        onClick={() => setRevAttachments(p => p.filter(x => x.key !== a.key))}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#1e40af', fontSize: '12px', padding: '0 2px' }}
                      >✕</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={handleSubmitRevision}
                disabled={saving || revUploading}
                style={{ ...S.btn, ...S.btnPrimary, opacity: (saving || revUploading) ? 0.7 : 1 }}
              >
                {saving ? 'Submitting...' : `Submit Rev ${(variation.current_revision || 0) + 1}`}
              </button>
              <button
                onClick={() => { setShowRevisionForm(false); setRevValue(''); setRevNotes(''); setRevAttachments([]); }}
                style={{ ...S.btn, ...S.btnGhost }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── CURRENT VQ DOCUMENTS ── */}
      <div style={{ ...S.card, marginBottom: '20px' }}>
        <h3 style={{ ...S.sectionTitle, marginBottom: '16px' }}>
          VQ Documents
          {latestRev && (
            <span style={{ fontSize: '12px', color: C.textMuted, fontWeight: '400', marginLeft: '8px' }}>
              Rev {latestRev.revision_number}
            </span>
          )}
        </h3>
        {isProlineStaff && (
          <div style={{ marginBottom: '16px' }}>
            <AttachmentUploader
              recordId={variation.id}
              bucket="variation-attachments"
              tableName="variation_attachments"
              foreignKey="variation_id"
              fileTypeField="file_type"
              onUploadSuccess={fetchAttachments}
              label="VQ documents"
            />
          </div>
        )}
        {currentRevAttachments.length === 0 ? (
          <div style={{ color: C.textMuted, fontSize: '14px', padding: '8px 0' }}>No documents uploaded.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {currentRevAttachments.map(att => (
              <div key={att.id} style={{
                display: 'flex', alignItems: 'center', gap: '12px',
                padding: '10px 14px', borderRadius: '6px',
                border: `1px solid ${C.border}`, background: '#fafafa',
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '13px', fontWeight: '500', color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {att.file_name}
                  </div>
                  <div style={{ fontSize: '11px', color: C.textMuted }}>
                    {formatBytes(att.file_size)} · {formatDate(att.uploaded_at)}
                  </div>
                </div>
                <FileTypeBadge type={att.attachment_type} />
                <button
                  onClick={() => handleDownload(att)}
                  style={{ ...S.btn, ...S.btnOutline, ...S.btnSm }}
                >
                  ↓ Download
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── LINKED DAYWORKS ── */}
      {linkedDayworks.length > 0 && (
        <div style={{ ...S.card, marginBottom: '20px' }}>
          <h3 style={{ ...S.sectionTitle, marginBottom: '12px' }}>Linked Dayworks ({linkedDayworks.length})</h3>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {linkedDayworks.map(dw => {
              const st = getStatusBadgeStyle(dw.status);
              return (
                <div
                  key={dw.id}
                  onClick={() => navigate('daywork-detail', { dayworkId: dw.id })}
                  style={{
                    padding: '8px 12px', background: '#f9fafb', borderRadius: '6px',
                    border: `1px solid ${C.border}`, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: '8px', transition: 'border-color 0.1s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = C.accent}
                  onMouseLeave={e => e.currentTarget.style.borderColor = C.border}
                >
                  <span style={{ fontWeight: '600', color: C.accent, fontSize: '13px' }}>{dw.reference}</span>
                  <span style={{ fontSize: '12px', color: C.textMuted }}>{formatDate(dw.date_of_works)}</span>
                  <span style={{ ...S.badge, ...st }}>{dw.status}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── MC RESPONSE PANEL ── */}
      {variation.mc_response ? (
        <div style={{
          ...S.card, marginBottom: '20px',
          border: `2px solid ${variation.mc_response === 'agreed' ? C.successBorder : variation.mc_response === 'disputed' ? C.dangerBorder : C.warningBorder}`,
        }}>
          <h3 style={{ ...S.sectionTitle, marginBottom: '12px' }}>MC Response</h3>
          <div style={{
            padding: '14px 16px', borderRadius: '6px',
            background: variation.mc_response === 'agreed' ? C.successLight : variation.mc_response === 'disputed' ? C.dangerLight : C.warningLight,
          }}>
            <div style={{ fontWeight: '700', fontSize: '15px', marginBottom: '8px' }}>
              {variation.mc_response?.replace(/_/g, ' ').toUpperCase()}
            </div>
            {(() => {
              const responderName = varResponseApproval?.full_name_confirmed || variation.mc_responded_by_profile?.full_name;
              const responderCompany = varResponseApproval?.company_confirmed || variation.mc_responded_by_profile?.company;
              return responderName
                ? <div style={{ fontSize: '14px', marginBottom: '4px' }}>Responded by: <strong>{responderName}{responderCompany ? ` — ${responderCompany}` : ''}</strong></div>
                : <div style={{ fontSize: '13px', marginBottom: '4px', color: C.danger }}>Responder details not recorded</div>;
            })()}
            {variation.mc_response_value && (
              <div style={{ fontSize: '14px', marginBottom: '4px' }}>
                Agreed Value: <strong>{formatCurrency(variation.mc_response_value)}</strong>
              </div>
            )}
            {variation.mc_response_reason && (
              <div style={{ fontSize: '14px', marginBottom: '4px', lineHeight: '1.6' }}>
                {variation.mc_response_reason}
              </div>
            )}
            {variation.mc_response_at && (
              <div style={{ fontSize: '12px', color: C.textMuted }}>
                Responded: {formatDateTime(variation.mc_response_at)}
              </div>
            )}
          </div>
        </div>
      ) : isMcQs && variation.status === 'submitted' ? (
        <div style={{ ...S.card, marginBottom: '20px' }}>
          <h3 style={{ ...S.sectionTitle, marginBottom: '16px' }}>MC Response</h3>
          {!showResponseForm ? (
            <button onClick={() => setShowResponseForm(true)} style={{ ...S.btn, ...S.btnPrimary }}>
              Respond to Variation
            </button>
          ) : (
            <div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', marginBottom: '16px' }}>
                {[
                  { value: 'agreed', label: 'Agreed', color: C.success },
                  { value: 'part_agreed', label: 'Part Agreed', color: C.warning },
                  { value: 'disputed', label: 'Disputed', color: C.danger },
                ].map(opt => (
                  <div
                    key={opt.value}
                    onClick={() => setMcResponse(opt.value)}
                    style={{
                      padding: '14px 12px', border: `2px solid ${mcResponse === opt.value ? opt.color : C.border}`,
                      borderRadius: '6px', cursor: 'pointer', textAlign: 'center',
                      fontWeight: '700', color: mcResponse === opt.value ? opt.color : C.textMuted,
                      background: mcResponse === opt.value ? '#f9fafb' : '#fff',
                      transition: 'all 0.1s',
                    }}
                  >
                    {opt.label}
                  </div>
                ))}
              </div>
              {mcResponse === 'part_agreed' && (
                <div style={S.formGroup}>
                  <label style={S.label}>Agreed Value (£)</label>
                  <input
                    type="number" step="0.01" style={S.input}
                    value={mcResponseValue}
                    onChange={e => setMcResponseValue(e.target.value)}
                    placeholder="0.00"
                  />
                </div>
              )}
              {(mcResponse === 'disputed' || mcResponse === 'part_agreed') && (
                <div style={S.formGroup}>
                  <label style={S.label}>
                    {mcResponse === 'disputed' ? 'Reason for Dispute *' : 'Reason / Explanation *'}
                  </label>
                  <textarea
                    style={S.textarea}
                    value={mcResponseReason}
                    onChange={e => setMcResponseReason(e.target.value)}
                    placeholder="Explain your assessment..."
                    rows={3}
                  />
                </div>
              )}
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={handleMcResponse}
                  disabled={saving}
                  style={{
                    ...S.btn,
                    background: mcResponse === 'agreed' ? C.success : mcResponse === 'part_agreed' ? C.warning : C.danger,
                    color: '#fff',
                    opacity: saving ? 0.7 : 1,
                  }}
                >
                  {saving ? 'Saving...' : 'Confirm Response'}
                </button>
                <button onClick={() => setShowResponseForm(false)} style={{ ...S.btn, ...S.btnGhost }}>
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      ) : null}

      {/* ── PROOF CHAIN ── */}
      <div style={{ ...S.card, marginBottom: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <div>
            <h3 style={{ ...S.sectionTitle, margin: '0 0 4px 0' }}>Proof Chain</h3>
            <div style={{ fontSize: '13px', color: C.textMuted }}>
              {confirmedCount} of {totalSteps} steps confirmed
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ width: '100px', height: '8px', background: '#e5e7eb', borderRadius: '4px', overflow: 'hidden' }}>
              <div style={{
                height: '100%',
                width: `${(confirmedCount / totalSteps) * 100}%`,
                background: confirmedCount === totalSteps ? C.success : confirmedCount >= 3 ? C.warning : C.accent,
                borderRadius: '4px', transition: 'width 0.3s',
              }} />
            </div>
            <span style={{ fontSize: '13px', fontWeight: '600', color: C.text }}>
              {Math.round((confirmedCount / totalSteps) * 100)}%
            </span>
            {isProlineStaff && (
              <button
                onClick={() => proofEditing ? handleSaveProofChain() : setProofEditing(true)}
                disabled={saving}
                style={{ ...S.btn, ...S.btnPrimary, ...S.btnSm, opacity: saving ? 0.7 : 1 }}
              >
                {saving ? 'Saving...' : proofEditing ? 'Save' : 'Edit'}
              </button>
            )}
            {proofEditing && (
              <button
                onClick={() => {
                  setProofEditing(false);
                  setProofChain({
                    instruction_status: variation.instruction_status || 'pending',
                    entitlement_status: variation.entitlement_status || 'pending',
                    quantum_status: variation.quantum_status || 'pending',
                    submission_status: variation.submission_status || 'pending',
                    application_status: variation.application_status || 'pending',
                  });
                }}
                style={{ ...S.btn, ...S.btnGhost, ...S.btnSm }}
              >
                Cancel
              </button>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {PROOF_CHAIN_ITEMS.map(item => {
            const status = proofChain[item.key] || 'pending';
            const colorMap = {
              confirmed: { border: C.successBorder, bg: C.successLight, dot: C.success },
              disputed: { border: C.dangerBorder, bg: C.dangerLight, dot: C.danger },
              pending: { border: C.warningBorder, bg: C.warningLight, dot: C.warning },
              na: { border: C.border, bg: '#f9fafb', dot: C.textLight },
            };
            const s = colorMap[status] || colorMap.pending;
            return (
              <div key={item.key} style={{
                display: 'flex', alignItems: 'flex-start', gap: '12px',
                padding: '12px 14px', border: `2px solid ${s.border}`,
                borderRadius: '8px', background: s.bg,
              }}>
                <ProofDot status={status} />
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px', flexWrap: 'wrap' }}>
                    <div>
                      <div style={{ fontWeight: '700', fontSize: '14px', color: C.text }}>{item.label}</div>
                      <div style={{ fontSize: '12px', color: C.textMuted, lineHeight: '1.5', marginTop: '2px' }}>{item.hint}</div>
                    </div>
                    {proofEditing && isProlineStaff ? (
                      <select
                        value={status}
                        onChange={e => setProofChain(pc => ({ ...pc, [item.key]: e.target.value }))}
                        style={{ ...S.select, width: '130px', fontSize: '13px' }}
                      >
                        {PROOF_STATUS_OPTIONS.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    ) : (
                      <span style={{
                        padding: '3px 10px', borderRadius: '10px', fontSize: '12px', fontWeight: '600',
                        background: '#fff', color: s.dot, border: `1px solid ${s.border}`,
                      }}>
                        {status.charAt(0).toUpperCase() + status.slice(1)}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── SUBMISSION HISTORY ── */}
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', marginBottom: '20px', overflow: 'hidden' }}>
        <div style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb' }}>
          <span style={{ fontSize: '11px', fontWeight: '700', letterSpacing: '0.08em', color: '#64748b', textTransform: 'uppercase' }}>Submission History</span>
        </div>
        {submissionLog.length === 0 ? (
          <div style={{ padding: '14px 16px', fontSize: '13px', color: '#9ca3af', fontStyle: 'italic' }}>Not yet submitted</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ ...S.table, margin: 0, borderRadius: 0 }}>
              <thead>
                <tr>
                  <th style={{ ...S.th, fontSize: '11px' }}>Date &amp; Time</th>
                  <th style={{ ...S.th, fontSize: '11px' }}>Sent By</th>
                  <th style={{ ...S.th, fontSize: '11px' }}>Sent To</th>
                </tr>
              </thead>
              <tbody>
                {submissionLog.map(entry => (
                  <tr key={entry.id}>
                    <td style={{ ...S.td, whiteSpace: 'nowrap', fontSize: '13px' }}>{formatDateTime(entry.sent_at)}</td>
                    <td style={{ ...S.td, fontSize: '13px' }}>{entry.profiles?.full_name || '—'}</td>
                    <td style={S.td}>
                      <div style={{ fontWeight: '600', fontSize: '13px' }}>{entry.sent_to_name}{entry.sent_to_company ? `, ${entry.sent_to_company}` : ''}</div>
                      <div style={{ fontSize: '12px', color: C.textMuted }}>{entry.sent_to_email}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── PAYAPPS ── */}
      {isProlineStaff && (
        <div style={{
          ...S.card, marginBottom: '32px',
          border: variation.payapp_number ? `2px solid #f59e0b` : `1px solid ${C.border}`,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: payappEditing || variation.payapp_number ? '16px' : '0' }}>
            <div>
              <h3 style={{ ...S.sectionTitle, margin: '0 0 2px 0' }}>Payment Application</h3>
              {!payappEditing && !variation.payapp_number && (
                <p style={{ margin: '6px 0 0 0', fontSize: '13px', color: C.textMuted }}>
                  Not yet included in a payment application.
                </p>
              )}
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              {payappEditing ? (
                <>
                  <button onClick={handleSavePayApp} disabled={saving} style={{ ...S.btn, ...S.btnPrimary, ...S.btnSm, opacity: saving ? 0.7 : 1 }}>
                    {saving ? 'Saving…' : 'Save'}
                  </button>
                  <button
                    onClick={() => {
                      setPayappEditing(false);
                      setPayapp({
                        payapp_number: variation.payapp_number ?? '',
                        payapp_date: variation.payapp_date ?? '',
                        payapp_certified_amount: variation.payapp_certified_amount ?? '',
                      });
                    }}
                    style={{ ...S.btn, ...S.btnGhost, ...S.btnSm }}
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <button onClick={() => setPayappEditing(true)} style={{ ...S.btn, ...S.btnOutline, ...S.btnSm }}>
                  {variation.payapp_number ? 'Edit' : '+ Add to PayApp'}
                </button>
              )}
            </div>
          </div>

          {payappEditing ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px' }}>
              <div style={S.formGroup}>
                <label style={S.label}>PayApp Number</label>
                <input
                  type="number" min="1" style={S.input}
                  value={payapp.payapp_number}
                  onChange={e => setPayapp(p => ({ ...p, payapp_number: e.target.value }))}
                  placeholder="e.g. 3"
                />
              </div>
              <div style={S.formGroup}>
                <label style={S.label}>Application Date</label>
                <input
                  type="date" style={S.input}
                  value={payapp.payapp_date}
                  onChange={e => setPayapp(p => ({ ...p, payapp_date: e.target.value }))}
                />
              </div>
              <div style={S.formGroup}>
                <label style={S.label}>Certified Amount (£)</label>
                <input
                  type="number" step="0.01" style={S.input}
                  value={payapp.payapp_certified_amount}
                  onChange={e => setPayapp(p => ({ ...p, payapp_certified_amount: e.target.value }))}
                  placeholder="0.00"
                />
              </div>
            </div>
          ) : variation.payapp_number ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '12px' }}>
              <div style={{ background: '#fffbeb', padding: '12px 16px', borderRadius: '6px', border: '1px solid #fde68a' }}>
                <div style={{ fontSize: '11px', color: '#92400e', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>PayApp Number</div>
                <div style={{ fontSize: '24px', fontWeight: '700', color: '#92400e' }}>#{variation.payapp_number}</div>
              </div>
              {variation.payapp_date && (
                <div style={{ background: '#f0fdf4', padding: '12px 16px', borderRadius: '6px', border: '1px solid #bbf7d0' }}>
                  <div style={{ fontSize: '11px', color: C.success, fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>Application Date</div>
                  <div style={{ fontSize: '16px', fontWeight: '600', color: C.success }}>{formatDate(variation.payapp_date)}</div>
                </div>
              )}
              {variation.payapp_certified_amount != null && (
                <div style={{ background: '#eff6ff', padding: '12px 16px', borderRadius: '6px', border: '1px solid #bfdbfe' }}>
                  <div style={{ fontSize: '11px', color: C.accent, fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>Certified Amount</div>
                  <div style={{ fontSize: '20px', fontWeight: '700', color: C.accent }}>{formatCurrency(variation.payapp_certified_amount)}</div>
                </div>
              )}
            </div>
          ) : null}
        </div>
      )}

      {/* Toast */}
      {resendToast && (
        <div style={{ position: 'fixed', bottom: '24px', right: '24px', background: C.success, color: '#fff', padding: '12px 20px', borderRadius: '8px', fontSize: '14px', fontWeight: '600', boxShadow: '0 4px 12px rgba(0,0,0,0.2)', zIndex: 1000 }}>
          ✓ {resendToast}
        </div>
      )}
    </div>
  );
}
